/**
 * What the opt-in prompt remembers about a player's ANSWERS, per campaign.
 *
 * The prompt keeps asking until it gets an answer — leaving without answering is not one. Then:
 * - **Opt in** (anywhere: the prompt, a home card, the page's button) — never again.
 * - **First "Not now"** — not again this session; it comes back on a later visit (a new session).
 * - **Second "Not now"** — silenced for that campaign. The page's own Opt In button stays.
 * And across campaigns: two "Not now"s in one session quiet the prompt for the rest of that
 * session (browsing five campaigns must not mean five prompts in a row).
 *
 * Storage: ONE namespaced localStorage entry — the widget runs on the operator's page, so it owns
 * exactly one key there — `{ [playerHash]: { [campaignKey]: Answer } }`, pruned on every write of
 * answers older than `ANSWER_TTL_MS` so it cannot grow forever. The session is a random id in
 * sessionStorage (a new tab or a new visit is a new session) plus a per-session dismissal count.
 *
 * Every access is guarded: storage can be blocked, full or wiped (private mode, cleared site data).
 * Then the in-memory copies carry the state for the life of the page.
 */

const ANSWERS_KEY = "unibo-widget:opt-in-prompt:answers";
const SESSION_KEY = "unibo-widget:opt-in-prompt:session";
const DISMISSALS_KEY = "unibo-widget:opt-in-prompt:dismissals";
/** An answer this old is forgotten (campaigns rarely run that long; keeps the entry small). */
const ANSWER_TTL_MS = 120 * 24 * 60 * 60 * 1000;
/** "Not now"s in one session, across campaigns, before the prompt stays quiet for the session. */
export const SESSION_DISMISS_LIMIT = 2;
/** "Not now"s on one campaign before it is silenced for good. */
const CAMPAIGN_DISMISS_LIMIT = 2;

type Answer = {
  /** Opted in: never ask again. */
  joined?: true;
  /** How many times the player said "Not now" to this campaign. */
  dismissals?: number;
  /** The session of the last "Not now": no second ask until a later one. */
  session?: string;
  /** When the answer was last written (for pruning). */
  at: number;
};
type Answers = Record<string, Record<string, Answer>>;

let memoryAnswers: Answers = {};
let memorySession: string | null = null;
let memoryDismissals = 0;

const readAnswers = (): Answers => {
  try {
    const raw = window.localStorage.getItem(ANSWERS_KEY);
    if (!raw) return memoryAnswers;
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object"
      ? (parsed as Answers)
      : memoryAnswers;
  } catch {
    return memoryAnswers;
  }
};

const writeAnswer = (
  playerHash: string,
  campaignKey: string,
  update: (previous: Answer | undefined) => Answer
) => {
  const now = Date.now();
  const all = readAnswers();
  const mine = { ...(all[playerHash] ?? {}) };
  mine[campaignKey] = { ...update(mine[campaignKey]), at: now };
  for (const [key, answer] of Object.entries(mine)) {
    if (now - (answer?.at ?? 0) > ANSWER_TTL_MS) delete mine[key];
  }
  const next = { ...all, [playerHash]: mine };
  memoryAnswers = next;
  try {
    window.localStorage.setItem(ANSWERS_KEY, JSON.stringify(next));
  } catch {
    /* blocked or full: the in-memory copy carries it for this page load */
  }
};

/** This session's id — a new tab or a new visit is a new session. Storage is the truth; the
 *  in-memory copy is only the fallback for when storage is blocked. */
function currentSession(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      memorySession = existing;
      return existing;
    }
    const id = Math.random().toString(36).slice(2);
    window.sessionStorage.setItem(SESSION_KEY, id);
    memorySession = id;
    return id;
  } catch {
    memorySession ??= Math.random().toString(36).slice(2);
    return memorySession;
  }
}

/** Whether the prompt may ask about this campaign now, given the player's earlier answers. */
export function mayPromptForOptIn(
  playerHash: string,
  campaignKey: string
): boolean {
  const answer = readAnswers()[playerHash]?.[campaignKey];
  if (!answer || typeof answer !== "object") return true;
  if (answer.joined) return false;
  const dismissals = answer.dismissals ?? 0;
  if (dismissals >= CAMPAIGN_DISMISS_LIMIT) return false;
  // One "Not now": back on a later visit, not again this session.
  if (dismissals > 0 && answer.session === currentSession()) return false;
  return true;
}

/** The player opted in (from anywhere): never ask about this campaign again. */
export function recordOptInPromptJoined(
  playerHash: string,
  campaignKey: string
): void {
  writeAnswer(playerHash, campaignKey, () => ({ joined: true, at: 0 }));
}

/** The player said "Not now" to this campaign. */
export function recordOptInPromptDismissal(
  playerHash: string,
  campaignKey: string
): void {
  writeAnswer(playerHash, campaignKey, (previous) => ({
    dismissals: (previous?.dismissals ?? 0) + 1,
    session: currentSession(),
    at: 0,
  }));
  memoryDismissals = sessionDismissals() + 1;
  try {
    window.sessionStorage.setItem(DISMISSALS_KEY, String(memoryDismissals));
  } catch {
    /* blocked: the in-memory count carries it for this page load */
  }
}

/** "Not now"s this session, across campaigns. Storage is the truth; memory is the fallback. */
export function sessionDismissals(): number {
  try {
    const raw = window.sessionStorage.getItem(DISMISSALS_KEY);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    return memoryDismissals;
  }
}
